import { GetStaticPropsContext } from "next";
import Head from "next/head";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { Text } from "@shared/elements";
import { useGraphSWR } from "@shared/hooks";
import { toWebUrl } from "@shared/utils";

import { GetCallById, TGetCallById } from "__generated__/types";

import AuthenticationForm from "src/components/AuthenticationForm";
import BrandsGridDisplay from "src/components/BrandsGridDisplay";
import DefaultOpenGraphTags from "src/components/DefaultOpenGraphTags";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";

/**
 * This route wraps the AuthenticationForm component.
 * Which enables the user to login or signup via password or code.
 *
 * But we have multiple user groups which require different styles and texts
 * when loging in or signing up.
 * Therefore we extracted the login and signup logic into a seperate component and
 * wrap this component in multiple routes with custom designs and features.
 */
export default function SubmissionLogIn() {
  const t = useTranslations(
    `${constants.MESSAGE_PREFIX}pages.submission-login`,
  );
  const searchParams = useSearchParams();
  const call = useCallData(searchParams);

  return (
    <>
      <Head>
        <title key="title">Picter Contests – Login</title>
        <DefaultOpenGraphTags />
      </Head>
      <div className="min-h-full flex">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              {call && (
                <Header
                  orgName={call.organisation.name.en}
                  logoUrl={call.logoDefault}
                  logoAlt={
                    call.organisation.name
                      ? `${call.organisation.name}'s logo`
                      : "Organisation logo"
                  }
                  website={call.website}
                />
              )}
              <Text as="h2" size="3xl" className="mt-8" weight="bold">
                {t("title")}
              </Text>
            </div>

            <div className="mt-8">
              <div className="mt-6">
                <AuthenticationForm
                  withPoweredBy
                  withAcceptTerms
                  withProviderLogins
                  autoSignup
                />
              </div>
            </div>
          </div>
        </div>
        <BrandsGridDisplay />
      </div>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.submission-login`,
    ...BrandsGridDisplay.messages,
    ...AuthenticationForm.messages,
  ]);
  return {
    props: {
      messages,
    },
  };
}

type HeaderProps = {
  /**
   * The name of the organization
   */
  orgName?: string;
  /**
   * The URL to the call logo
   */
  logoUrl?: string;
  /**
   * Alternative text for the logo
   */
  logoAlt?: string;
  /**
   * The URL to the call website
   */
  website?: string | null;
};

function Header({ orgName, website, logoUrl, logoAlt }: HeaderProps) {
  if (!logoUrl) return null;

  const logo = (
    <>
      {orgName && <span className="sr-only">{orgName}</span>}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className="h-20 w-auto" src={logoUrl} alt={logoAlt} />
    </>
  );

  // The logo is unlinked when there is nothing we can follow — see toWebUrl
  const websiteUrl = website ? toWebUrl(website) : null;

  return (
    <header>
      {websiteUrl ? (
        <Link href={websiteUrl} target="_blank" rel="noopener noreferrer">
          {logo}
        </Link>
      ) : (
        logo
      )}
    </header>
  );
}

/**
 * Custom hook to fetch the call data from the API
 * It takes the searchParams from the router and extracts the callId from it.
 * If no callId is present or the data is not yet loaded , it returns undefined.
 */
function useCallData(searchParams?: URLSearchParams) {
  const callId = searchParams.get("callId") ?? "";

  const data = useGraphSWR<TGetCallById>(
    GetCallById,
    {
      id: callId as string,
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  if (!callId) return undefined;

  if (data.error) {
    console.error(data.error);
    return undefined;
  }
  return data.data?.call;
}
